import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import { Box, MenuItem, Typography, TextField, Autocomplete, Alert, TablePagination, createFilterOptions, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { DeleteForever as DeleteForeverIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { AdminPageContainer, StaticCard } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import ApiKeyList from '../../components/ApiKeyList';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { getApiKeyScopes, getAllApiKeys, revokeUserApiKey, deleteUserApiKey } from '../../redux/ducks/apiKey';
import { getAllUsers } from '../../redux/slices/users';
import { getClients } from '../../redux/ducks/client';
import { showAlert } from '../../redux/slices/alerts';
import { useHasPermission } from '../../utils/permissionUtils';
import { methodLabel } from '../../utils/apiScopeUtils';

const PickerCard = styled(StaticCard)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const PickerHelper = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const FilterRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
}));

const FilterField = styled(Box)({
    flex: '1 1 240px',
    minWidth: 0,
});

// Deliberately louder than the shared ConfirmDialog: revoking is reversible bookkeeping, this is not.
const DangerTitle = styled(DialogTitle)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
}));

const TargetBox = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
}));

const STATUS_OPTIONS = ['Active', 'Never used', 'Idle', 'Expired', 'Revoked'];

const userName = (user) => `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unnamed user';

// The directory runs to thousands of people, so match on name/email only and cap what is rendered.
const filterUsers = createFilterOptions({
    limit: 50,
    stringify: (user) => `${userName(user)} ${user?.email || ''}`,
});

const filterClients = createFilterOptions({ limit: 50, stringify: (client) => client?.name || '' });

const AdminApiKeys = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canEditUsers = hasPermission('editUsers');
    const canAdminUsers = hasPermission('adminUsers');

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(true);
    const [selectedClient, setSelectedClient] = useState(null);
    const [status, setStatus] = useState('');
    const [scopeDomain, setScopeDomain] = useState(null);
    const [scopeMethods, setScopeMethods] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [keysLoading, setKeysLoading] = useState(true);
    const [domains, setDomains] = useState([]);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Deleting a key and changing a filter both move `total` under us, so clamp before asking.
    const lastPage = total > 0 ? Math.ceil(total / limit) - 1 : 0;
    const effectivePage = Math.min(page, lastPage);

    const params = useMemo(() => ({
        userId: selectedUser?._id || undefined,
        client: selectedClient?._id || undefined,
        status: status || undefined,
        scopeDomain: scopeDomain?.domain || undefined,
        scopeMethods: scopeDomain && scopeMethods.length ? scopeMethods.join(',') : undefined,
        start: effectivePage * limit,
        limit,
    }), [selectedUser, selectedClient, status, scopeDomain, scopeMethods, effectivePage, limit]);

    const loadKeys = useCallback(() => {
        setKeysLoading(true);
        dispatch(getAllApiKeys(params, (data) => {
            setKeysLoading(false);
            setApiKeys(Array.isArray(data?.items) ? data.items : []);
            setTotal(data?.total || 0);
        }));
    }, [dispatch, params]);

    useEffect(() => {
        if (!canEditUsers) {
            setUsersLoading(false);
            setKeysLoading(false);
            return;
        }
        dispatch(getApiKeyScopes((data) => {
            if (data?.domains) setDomains(data.domains);
        }));
        dispatch(getAllUsers({
            callback: (data) => {
                setUsers(Array.isArray(data) ? data : []);
                setUsersLoading(false);
            },
        }));
        // limit 0 = every client, as the other client pickers do; a capped page would hide options.
        dispatch(getClients(0, 0, (data) => {
            setClients(Array.isArray(data) ? data : data?.clients || []);
            setClientsLoading(false);
        }, { active: true }));
    }, [dispatch, canEditUsers]);

    useEffect(() => {
        if (canEditUsers) loadKeys();
    }, [canEditUsers, loadKeys]);

    useEffect(() => {
        if (page !== effectivePage) setPage(effectivePage);
    }, [page, effectivePage]);

    const handleRevoke = () => {
        if (!revokeTarget?.user?._id) return;
        const target = revokeTarget;
        setRevokeTarget(null);
        dispatch(revokeUserApiKey(target.user._id, target._id, (data) => {
            if (data) dispatch(showAlert({ severity: 'success', message: `"${target.label}" was revoked.` }));
            loadKeys();
        }));
    };

    const handleDelete = () => {
        if (!deleteTarget?.user?._id) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        dispatch(deleteUserApiKey(target.user._id, target._id, (data) => {
            if (data) dispatch(showAlert({ severity: 'success', message: `"${target.label}" was permanently deleted.` }));
            loadKeys();
        }));
    };

    const hasFilters = !!(selectedUser || selectedClient || status || scopeDomain);

    if (!canEditUsers) {
        return (
            <AdminPageContainer>
                <PageHeader title="User API Keys" />
                <Alert severity="warning">You need the "edit users" permission to review other people's API keys.</Alert>
            </AdminPageContainer>
        );
    }

    return (
        <AdminPageContainer>
            <PageHeader
                title="User API Keys"
                subtitle="Every key people have issued for external tools. You can revoke a key here; only its owner can create one."
            />

            <PickerCard>
                <PickerHelper variant="body2" color="text.secondary">
                    All keys are listed below. Narrow the list with any combination of the filters. You only see people at or below your own role level.
                </PickerHelper>
                <FilterRow>
                    <FilterField>
                        <Autocomplete
                            options={users}
                            loading={usersLoading}
                            value={selectedUser}
                            onChange={(_, user) => {
                                setSelectedUser(user);
                                setPage(0);
                            }}
                            getOptionLabel={(user) => userName(user)}
                            filterOptions={filterUsers}
                            autoHighlight
                            isOptionEqualToValue={(option, selected) => option._id === selected._id}
                            renderOption={(props, option) => <li {...props} key={option._id}>{userName(option)}</li>}
                            renderInput={(params) => <TextField {...params} label="Person" placeholder="Everyone" helperText="Leave empty for all people" />}
                        />
                    </FilterField>
                    <FilterField>
                        <Autocomplete
                            options={clients}
                            loading={clientsLoading}
                            value={selectedClient}
                            onChange={(_, client) => {
                                setSelectedClient(client);
                                setPage(0);
                            }}
                            getOptionLabel={(client) => client?.name || ''}
                            filterOptions={filterClients}
                            autoHighlight
                            isOptionEqualToValue={(option, selected) => option._id === selected._id}
                            renderOption={(props, option) => <li {...props} key={option._id}>{option.name}</li>}
                            renderInput={(params) => <TextField {...params} label="Client" placeholder="Every client" helperText="The key owner's client" />}
                        />
                    </FilterField>
                    <FilterField>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={status}
                            onChange={(e) => {
                                setStatus(e.target.value);
                                setPage(0);
                            }}
                            helperText="Any status unless you pick one"
                        >
                            <MenuItem value="">Any status</MenuItem>
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                    </FilterField>
                    <FilterField>
                        <Autocomplete
                            options={domains}
                            value={scopeDomain}
                            onChange={(_, entry) => {
                                setScopeDomain(entry);
                                setScopeMethods([]);
                                setPage(0);
                            }}
                            getOptionLabel={(entry) => entry?.label || ''}
                            autoHighlight
                            isOptionEqualToValue={(option, selected) => option.domain === selected.domain}
                            renderOption={(props, option) => <li {...props} key={option.domain}>{option.label}</li>}
                            renderInput={(params) => <TextField {...params} label="Permission area" placeholder="Any area" helperText="Keys that can reach this area" />}
                        />
                    </FilterField>
                    <FilterField>
                        <Autocomplete
                            multiple
                            disabled={!scopeDomain}
                            options={scopeDomain?.methods || []}
                            value={scopeMethods}
                            onChange={(_, methods) => {
                                setScopeMethods(methods);
                                setPage(0);
                            }}
                            getOptionLabel={(method) => methodLabel(method)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Actions"
                                    placeholder={scopeDomain ? 'Any action' : ''}
                                    helperText={scopeDomain ? 'Keys allowed any one of these' : 'Pick an area first'}
                                />
                            )}
                        />
                    </FilterField>
                </FilterRow>
            </PickerCard>

            <ApiKeyList
                apiKeys={apiKeys}
                domains={domains}
                loading={keysLoading}
                onRevoke={setRevokeTarget}
                onDelete={canAdminUsers ? setDeleteTarget : undefined}
                showIp
                showUser
                emptyText={hasFilters ? 'No API keys match these filters.' : 'Nobody has created an API key yet.'}
            />

            {total > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={effectivePage}
                    onPageChange={(e, next) => setPage(next)}
                    rowsPerPage={limit}
                    rowsPerPageOptions={[10, 25, 50]}
                    onRowsPerPageChange={(e) => {
                        setLimit(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}

            <ConfirmDialog
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={handleRevoke}
                title="Revoke this API key?"
                confirmLabel="Revoke key"
                message={`"${revokeTarget?.label}" belonging to ${revokeTarget?.user?.fullName || 'this person'} will stop working within 15 minutes and cannot be re-enabled. Whoever set it up will need to create a new one.`}
            />

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="sm" fullWidth>
                <DangerTitle>
                    <WarningAmberIcon />
                    Delete this API key for good?
                </DangerTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Revoking is the usual choice: it stops the key working but keeps the record. Delete only when the record itself has to go.
                    </Typography>
                    <TargetBox>
                        <Typography variant="body2" fontWeight="bold">{deleteTarget?.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {deleteTarget?.user?.fullName || 'Unknown owner'} · {deleteTarget?.user?.email || 'no email'}
                        </Typography>
                    </TargetBox>
                    <Alert severity="error">
                        The key is removed permanently and cannot be brought back. Past log entries that mention it will no longer resolve to any key. If it is still in
                        use, whatever is using it stops working within 15 minutes.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)}>Keep the key</Button>
                    <Button variant="contained" color="error" startIcon={<DeleteForeverIcon />} onClick={handleDelete}>
                        Delete permanently
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageContainer>
    );
};

export default AdminApiKeys;
